import React, { useState, useLayoutEffect, useEffect } from "react";
import {
  Form,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import FormField from "../components/FormField";
import LoaderButton from "../components/LoaderButton";
import { getUserDetails, updateUserProfile, changeUserPassword } from "../actions/userActions";

const regexNum = /^\d+$/;

const ProfileScreen = () => {
  const dispatch = useDispatch();
  const { loading, error, user = {} } = useSelector(state => state.userDetails);
  const { success } = useSelector(state => state.updateUser);
  const { userInfo } = useSelector(state => state.userLogin);

  const [activeTab, setActiveTab] = useState("profile");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [address3, setAddress3] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [landline, setLandline] = useState("");
  const [officeEmail, setOfficeEmail] = useState("");
  const [newpass, setNewpass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [validated, setValidated] = useState(false);

  useLayoutEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setAddress1(user.address_line1 || "");
      setAddress2(user.address_line2 || "");
      setAddress3(user.address_line3 || "");
      setContactNumber(user.contact_number || "");
      setLandline(user.landline || "");
      setOfficeEmail(user.officeEmail || "");
    }
  }, [user]);

  const submitHandler = e => {
    e.preventDefault();
    dispatch(updateUserProfile({
      address1, address2, address3, contactNumber, landline, officeEmail
    }));
  };

  const changePasswordHandler = e => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() && newpass === confirmPass) {
      dispatch(changeUserPassword({ newPassword: newpass }));
    }
    setValidated(true);
  };

  const handleNumberChange = setter => e => {
    const value = e.target.value;
    if (!value || regexNum.test(value)) setter(value);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin": return "Administrator";
      case "lab": return "Lab Manager";
      default: return "User";
    }
  };

  return (
    <div className="profile-screen pb-5">

      {/* Profile Card with Tabs */}
      <div className="profile-card">

        {/* Top Section: User info + Tab strip */}
        <div className="profile-card-header">
          <div className="d-flex align-items-center mb-3">
            <div className="profile-avatar-circle">
              {(user?.user_name || userInfo?.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="ml-3">
              <h5 className="font-weight-bold mb-0">{user?.user_name || userInfo?.name || "User"}</h5>
              <span className="text-muted small">{userInfo?.email || ""}</span>
              <span className="profile-role-pill ml-2">{getRoleLabel(userInfo?.role)}</span>
            </div>
          </div>

          {/* Tab Strip */}
          <div className="profile-tabs">
            <button
              className={`profile-tab-btn ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => setActiveTab("profile")}
            >
              <i className="fa fa-user mr-2"></i>Profile
            </button>
            <button
              className={`profile-tab-btn ${activeTab === "address" ? "active" : ""}`}
              onClick={() => setActiveTab("address")}
            >
              <i className="fa fa-map-marker mr-2"></i>Address
            </button>
            <button
              className={`profile-tab-btn ${activeTab === "security" ? "active" : ""}`}
              onClick={() => setActiveTab("security")}
            >
              <i className="fa fa-lock mr-2"></i>Security
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="profile-card-body">

          {/* ===== Profile Tab ===== */}
          {activeTab === "profile" && (
            <div className="profile-pane-animate" key="profile">
              <div className="profile-section-header">
                <h6 className="font-weight-bold mb-0">Personal Information</h6>
                <p className="text-muted small mb-0">Your basic account details</p>
              </div>
              <div className="profile-info-grid">
                <div className="profile-info-item">
                  <span className="profile-info-label">Full Name</span>
                  <span className="profile-info-value">{user?.user_name || userInfo?.name || "—"}</span>
                </div>
                <div className="profile-info-item">
                  <span className="profile-info-label">Email</span>
                  <span className="profile-info-value">{user?.email || userInfo?.email || "—"}</span>
                </div>
                <div className="profile-info-item">
                  <span className="profile-info-label">Mobile</span>
                  <span className="profile-info-value">{user?.mobile || "—"}</span>
                </div>
                <div className="profile-info-item">
                  <span className="profile-info-label">Role</span>
                  <span className="profile-info-value">
                    <span className="profile-role-pill">{getRoleLabel(userInfo?.role)}</span>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ===== Address Tab ===== */}
          {activeTab === "address" && (
            <div className="profile-pane-animate" key="address">
              <div className="profile-section-header">
                <h6 className="font-weight-bold mb-0">Address & Contact</h6>
                <p className="text-muted small mb-0">Update your address and contact information</p>
              </div>

              {error && <Alert variant="danger" className="border-0 rounded-lg mb-3">{error}</Alert>}
              {success && (
                <Alert variant="success" className="border-0 rounded-lg mb-3">
                  <i className="fa fa-check-circle mr-2"></i>Updated successfully!
                </Alert>
              )}

              <Form onSubmit={submitHandler}>
                <Row>
                  <Col md={6}>
                    <FormField label="Address Line 1" mandatory>
                      <Form.Control type="text" className="profile-input" placeholder="Street address"
                        value={address1} maxLength={100} onChange={e => setAddress1(e.target.value)} required />
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField label="Address Line 2">
                      <Form.Control type="text" className="profile-input" placeholder="Apartment, suite, etc."
                        value={address2} maxLength={100} onChange={e => setAddress2(e.target.value)} />
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField label="City / Area">
                      <Form.Control type="text" className="profile-input" placeholder="City or area"
                        value={address3} maxLength={100} onChange={e => setAddress3(e.target.value)} />
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField label="Contact Number">
                      <Form.Control type="tel" className="profile-input" placeholder="Mobile number"
                        value={contactNumber} maxLength={11} onChange={handleNumberChange(setContactNumber)} />
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField label="Landline">
                      <Form.Control type="tel" className="profile-input" placeholder="Landline number"
                        value={landline} maxLength={11} onChange={handleNumberChange(setLandline)} />
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField label="Email Address">
                      <Form.Control type="email" className="profile-input" placeholder="Office email"
                        value={officeEmail} maxLength={100} onChange={e => setOfficeEmail(e.target.value)} />
                    </FormField>
                  </Col>
                </Row>
                <div className="profile-form-actions">
                  <LoaderButton isLoading={loading} type="submit" variant="primary" disabled={!address1 || address1.length <= 5}>
                    Save Changes
                  </LoaderButton>
                </div>
              </Form>
            </div>
          )}

          {/* ===== Security Tab ===== */}
          {activeTab === "security" && (
            <div className="profile-pane-animate" key="security">
              <div className="profile-section-header">
                <h6 className="font-weight-bold mb-0">Change Password</h6>
                <p className="text-muted small mb-0">Keep your account secure with a strong password</p>
              </div>

              {error && <Alert variant="danger" className="border-0 rounded-lg mb-3">{error}</Alert>}
              {success && (
                <Alert variant="success" className="border-0 rounded-lg mb-3">
                  <i className="fa fa-check-circle mr-2"></i>Password updated!
                </Alert>
              )}

              <Form onSubmit={changePasswordHandler} noValidate validated={validated}>
                <Row>
                  <Col md={6}>
                    <FormField label="New Password" mandatory>
                      <Form.Control type="password" className="profile-input" placeholder="Minimum 8 characters"
                        value={newpass} minLength={8} maxLength={20} onChange={e => setNewpass(e.target.value)} required />
                      <Form.Control.Feedback type="invalid">At least 8 characters required.</Form.Control.Feedback>
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField label="Confirm Password" mandatory>
                      <Form.Control type="password" className="profile-input" placeholder="Re-enter password"
                        value={confirmPass} maxLength={20} minLength={8} onChange={e => setConfirmPass(e.target.value)}
                        required isInvalid={validated && newpass !== confirmPass} />
                      <Form.Control.Feedback type="invalid">Passwords do not match.</Form.Control.Feedback>
                    </FormField>
                  </Col>
                </Row>
                <div className="profile-form-actions">
                  <LoaderButton isLoading={loading} type="submit" variant="primary">
                    Update Password
                  </LoaderButton>
                </div>
              </Form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
